import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { Box, CircularProgress } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MonthDistributionComponent from '../../components/ProjectsDistribution/MonthDistribution'
import { useAppDispatch, useAppSelector, useProjectDistributionDateRange } from '../../store/redux/hooks'
import { setLeaderId } from '../../store/redux/reducers/leaderSlice'
import { pageHeaderActions } from '../../store/redux/reducers/pageHeaderSlice'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import { WorkforceRequestProps } from '../../store/redux/reducers/workforceScheduleSlice'
import DateFormat from '../../store/types/enums/DateFormat'
import { StyledPageContent } from '../../styles/PageStyles'
import { RoundButton } from '../../styles/ButtonStyles'
import { useSelector } from 'react-redux'
import { getCurrentUser, getIsManagement } from '../../store/redux/reducers/currentUserSlice'

const ProjectsDistributionView = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { setFromDate, setToDate } = useProjectDistributionDateRange()
  const { from, to } = useAppSelector(state => state.projectDistributionDateRange)
  const [, setRequestProps] = useState<WorkforceRequestProps>({
    fromDate: from,
    toDate: to,
  })
  const { leaderId } = useAppSelector(state => state.leaderId)
  const currentUser = useSelector(getCurrentUser)
  const isManagement = useSelector(getIsManagement)

  useEffect(() => {
    if (currentUser) {
      dispatch(setLeaderId(currentUser.id))
    }
  }, [currentUser, dispatch])

  useEffect(() => {
    if (!leaderId && currentUser) {
      dispatch(setLeaderId(currentUser.id))
    }
  }, [leaderId, dispatch, currentUser])

  useEffect(() => {
    dispatch(setPageName(t('page.name.projectsDistribution')))
    dispatch(setPageIndex(PageIndex.PROJECTS_DISTRIBUTION))

    dispatch(
      pageHeaderActions.setButtons({
        monthPicker: false,
        monthRangePicker: { shown: true },
        leaderPicker: { shown: isManagement, onlyProjectLeaders: true },
      })
    )

    return () => {
      dispatch(pageHeaderActions.setInitialState())
    }
  }, [dispatch, t, isManagement])

  const getInterval = () => {
    let interval: Dayjs[] = []
    let currentDate = dayjs(from)

    while (currentDate.isBefore(to) || currentDate.isSame(to)) {
      interval.push(currentDate)
      currentDate = currentDate.add(1, 'month')
    }

    return interval
  }

  const handleArrowUpClick = () => {
    const fromDate = dayjs(from)
    const toDate = dayjs(to)
    setFromDate(fromDate.subtract(1, 'month').format(DateFormat.YearMonth))
    setToDate(toDate.subtract(1, 'month').format(DateFormat.YearMonth))
  }

  const handleArrowDownClick = () => {
    const fromDate = dayjs(from)
    const toDate = dayjs(to)
    setFromDate(fromDate.add(1, 'month').format(DateFormat.YearMonth))
    setToDate(toDate.add(1, 'month').format(DateFormat.YearMonth))
  }

  useEffect(() => {
    setRequestProps(prev => ({ ...prev, fromDate: from, toDate: to }))
  }, [from, to])

  return (
    <StyledPageContent color="text.light" sx={{ height: '90%', paddingBottom: 3 }}>
      {!currentUser || !leaderId ? (
        <Box display={'flex'} width={'100%'} height={'100%'} justifyContent={'center'} alignItems={'center'}>
          <CircularProgress />
        </Box>
      ) : (
        <Box display={'flex'} flexDirection={'column'} justifyContent={'space-around'} height={'100%'}>
          <RoundButton
            aria-label={t('page.projectDistribution.aria.buttonMonthUp')}
            color="primary"
            onClick={handleArrowUpClick}
            sx={{ alignSelf: 'center', mt: 2 }}
          >
            <KeyboardArrowUpIcon />
          </RoundButton>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '80px', overflow: 'auto', maxHeight: 770 }}>
            {getInterval().map(date => {
              return <MonthDistributionComponent key={date.format(DateFormat.YearMonth)} date={date} />
            })}
          </Box>

          <RoundButton
            aria-label={t('page.projectDistribution.aria.buttonMonthDown')}
            color="primary"
            onClick={handleArrowDownClick}
            sx={{ alignSelf: 'center', mt: 2 }}
          >
            <KeyboardArrowDownIcon />
          </RoundButton>
        </Box>
      )}
    </StyledPageContent>
  )
}

export default ProjectsDistributionView